import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { decodeRegionFromId } from '@app/shared/helpers/helpers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Transfer, CreateTeamUserDropboxOutput, CreateTeamDropboxInput, CreateTeamUserDropboxInput, CreateTeamDropboxOutput } from '@smash-sdk/transfer/ejs/01-2024';
import { config, Region } from '@smash-sdk/core';
import { from, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { DropboxActions } from '../actions';
import GetDropboxPreviewError from '@smash-sdk/transfer/ejs/01-2024/types/GetDropboxPreview/GetDropboxPreviewError';
import { TokenService } from '@app/core/services/token.service';

@Injectable()
export class DropboxEffects {

  constructor(
    private actions$: Actions,
    private store$: Store<fromRoot.State>,
    private router: Router,
    private tokenService: TokenService
  ) { }

  createTeamDropbox$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropboxActions.CreateTeamDropbox),
      withLatestFrom(
        this.store$.pipe(select(fromRoot.getActiveIdentity)),
        this.store$.pipe(select(fromRoot.getCurrentDomain)),
      ),
      concatMap(([action, identity, domain]) => {
        const transferSdk = new Transfer();
        const params: CreateTeamDropboxInput | CreateTeamUserDropboxInput = {
          teamId: action.teamId,
          configuration: { transfer: domain.options?.dropbox?.transfer } || { transfer: Constant.defaultPremiumDomainSettings.dropbox.transfer },
          maxTransferCount: domain.options?.dropbox?.maxTransferCount?.default || Constant.defaultPremiumDomainSettings.dropbox.maxTransferCount.default,
        };

        if (domain.options?.dropbox?.availabilityDuration?.default) {
          params.availabilityDuration = domain.options?.dropbox?.availabilityDuration?.default;
        }

        const isRoot = this.tokenService.isRoot(identity.token.token);

        if (!isRoot) {
          (params as CreateTeamUserDropboxInput).userId = identity.id;
        }

        const req = isRoot ? transferSdk.createTeamDropbox(params as CreateTeamDropboxInput) : transferSdk.createTeamUserDropbox(params as CreateTeamUserDropboxInput);

        return from(req).pipe(
          map((resp: CreateTeamDropboxOutput | CreateTeamUserDropboxOutput) => DropboxActions.CreateTeamDropboxSuccess({ dropbox: resp.dropbox })),
          catchError(error => of(DropboxActions.CreateTeamDropboxFailure({ error: error.message })))
        );
      }))
  );

  getDropboxPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropboxActions.GetDropboxPreview),
      exhaustMap((action) => {
        const region = decodeRegionFromId(action.dropboxId);
        config.setRegion(region as Region);
        const transferSdk = new Transfer({ region: region as Region });
        return from(transferSdk.getDropboxPreview({ dropboxId: action.dropboxId })).pipe(
          map(({ dropbox }) => {
            if (dropbox.status === 'Enabled') {
              return DropboxActions.GetDropboxPreviewSuccess({ dropbox });
            } else if (dropbox.status === 'Disabled') {
              this.router.navigateByUrl('/request-expired', { skipLocationChange: true });
              return DropboxActions.GetDropboxPreviewSuccess({ dropbox });
            } else if (dropbox.status === 'Deleted') {
              this.router.navigateByUrl('/request-expired', { skipLocationChange: true });
              return DropboxActions.GetDropboxPreviewSuccess({ dropbox });
            }
          }),
          catchError(error => {
            if (error instanceof GetDropboxPreviewError.NotFoundError) {
              this.router.navigateByUrl('/request-expired', { skipLocationChange: true });
            } else if (error instanceof GetDropboxPreviewError.OwnerUsageExceededError) {
              this.router.navigateByUrl('/request-expired', { skipLocationChange: true });
            } else if (error instanceof GetDropboxPreviewError.DropboxDeletedError) {
              this.router.navigateByUrl('/request-expired', { skipLocationChange: true });
            } else if (error instanceof GetDropboxPreviewError.DropboxDisabledError) {
              this.router.navigateByUrl('/request-expired', { skipLocationChange: true });
            } else if (error instanceof GetDropboxPreviewError.DropboxMaxTransferCountError) {
              this.router.navigateByUrl('/request-expired', { skipLocationChange: true });
            } else if (error instanceof GetDropboxPreviewError.DropboxMaxBytesUploadedError) {
              this.router.navigateByUrl('/request-expired', { skipLocationChange: true });
            } else {
              this.router.navigateByUrl('/request-expired', { skipLocationChange: true });
            }
            return of(DropboxActions.GetDropboxPreviewFailure({ error: error.message }));
          })
        );
      })
    )
  );

}
